import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getAppSocket } from '@/lib/wesSocket';

type Supply = {
  id: string;
  name: string;
  stock: number;
  minStock: number;
  location?: string;
  category?: string;
};

const SupplyStock: React.FC = () => {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const fetchSupplies = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/supplies', { params: { page: 1, limit: 500 } });
      setSupplies(res.data?.supplies || res.data || []);
    } catch (error: any) {
      console.error('Error fetching supplies stock', error);
      toast.error(error?.response?.data?.error || 'No se pudo cargar el stock de insumos');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleGeneratePdf = () => {
    setGeneratingPdf(true);
    toast.error('Generar PDF de insumos no esta disponible.');
    setGeneratingPdf(false);
  };

  useEffect(() => {
    fetchSupplies();
  }, []);

  useEffect(() => {
    const socket = getAppSocket();
    let refreshTimer: number | null = null;

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/supplies') || path.startsWith('/api/purchases') || path.startsWith('/api/service-reports'))) {
        return;
      }

      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchSupplies({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, []);

  const rows = supplies.filter((s) => s.stock > 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-7xl px-4 py-8 space-y-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Stock de Insumos</h2>
            <p className="text-sm text-muted-foreground">Listado de stock actual y minimos configurados.</p>
          </div>
          <Button variant="outline" onClick={handleGeneratePdf} disabled={generatingPdf} className="h-9 rounded-lg px-4">
            {generatingPdf ? 'Generando...' : 'Generar PDF'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-4">
              <div className="rounded-lg border overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead>Ubicacion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((s) => {
                      const low = s.minStock > 0 && s.stock <= s.minStock;
                      const out = s.minStock > 0 && s.stock <= 0;
                      return (
                        <TableRow key={s.id} className="border-b hover:bg-muted/50">
                          <TableCell>{s.name}</TableCell>
                          <TableCell>{s.category || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <span>{s.stock}</span>
                              {low && (
                                <Badge variant="secondary" className={out ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}>
                                  {out ? 'Sin stock' : 'Bajo'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{s.location || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SupplyStock;
